// src/admin/pages/FormDashboard.jsx
import {
  Beaker,
  Edit,
  ExternalLink,
  Eye,
  FolderIcon,
  Trash2,
  Users,
  Pencil,
} from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

import { PREFIX } from "../../router";
import {
  getHongKongDateStr,
  getHongKongTimeStr,
  parseHKTDatetimeStr,
} from "../../utils/datetimeUtil";

import { DRAFT_DELETE_URL, DRAFT_LIST_FORM_URL } from "../../constants/urls";

const FormTitle = ({ title, formId, isSubform }) => {
  const displayTitle = title || formId || "Untitled Form";

  return (
    <div className="relative">
      <div className="flex items-center gap-2 w-full">
        {isSubform && (
          <FolderIcon size={14} className="text-blue-600 flex-shrink-0" />
        )}
        <div className="min-w-0 w-full">
          <h2
            className="text-xs font-semibold text-gray-900 leading-tight line-clamp-4"
            title={displayTitle}
          >
            {displayTitle}
          </h2>
        </div>
      </div>
    </div>
  );
};

const FormDashboard = ({ mode = "form" }) => {
  // mode can be 'form', 'test', or 'draft'
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to get mode-specific configuration
  const getModeConfig = () => {
    switch (mode) {
      case "test":
        return {
          title: "Test Forms",
          icon: Beaker,
          badge: "Test Environment",
          badgeColor: "yellow",
          basePath: `${PREFIX}/test`,
          loadMethod: "index", // loads from index.json
        };
      case "draft":
        return {
          title: "Draft Forms",
          icon: Edit,
          badge: "Drafts",
          badgeColor: "blue",
          basePath: `${PREFIX}/draft`,
          loadMethod: "api", // loads from API
        };
      default:
        return {
          title: "Forms",
          icon: Users,
          basePath: `${PREFIX}/forms`,
          loadMethod: "index",
        };
    }
  };

  const config = getModeConfig();

  useEffect(() => {
    const loadForms = async () => {
      try {
        setLoading(true);
        setError(null);

        let loadedForms = [];

        if (config.loadMethod === "api") {
          // Load drafts directly from the list endpoint
          const response = await fetch(DRAFT_LIST_FORM_URL);
          if (!response.ok) {
            throw new Error("Failed to load drafts list");
          }
          const data = await response.json();
          loadedForms = data.drafts.map((draft) => draft.draft_data);
          setForms(loadedForms.filter((form) => form !== null));
        } else {
          // Load from index.json (for non-draft forms)
          const indexResponse = await fetch(`${config.basePath}/index.json`);
          if (!indexResponse.ok) {
            throw new Error(`Failed to load ${mode} forms index`);
          }
          const indexData = await indexResponse.json();

          // Handle both old and new format
          const formIds = Array.isArray(indexData)
            ? indexData
            : indexData.forms;
          const subformIds = Array.isArray(indexData)
            ? []
            : indexData.subforms || [];

          // Load main forms
          const mainFormsPromises = formIds.map(async (formId) => {
            try {
              const response = await fetch(`${config.basePath}/${formId}.json`);
              if (!response.ok) return null;
              const formData = await response.json();
              return { ...formData, isSubform: false };
            } catch (err) {
              console.error(`Error loading form ${formId}:`, err);
              return null;
            }
          });

          // Load subforms if they exist
          const subformsPromises = subformIds.map(async (filename) => {
            try {
              const response = await fetch(
                `${config.basePath}/subforms/${filename}`
              );
              if (!response.ok) return null;
              const formData = await response.json();
              return { ...formData, isSubform: true };
            } catch (err) {
              console.error(`Error loading subform ${filename}:`, err);
              return null;
            }
          });

          // Combine all forms
          loadedForms = await Promise.all([
            ...mainFormsPromises,
            ...subformsPromises,
          ]);
        }

        // Filter out any null results and set the forms
        setForms(loadedForms.filter((form) => form !== null));
      } catch (err) {
        console.error("Error loading forms:", err);
        setError(`Failed to load ${mode} forms. Please try again later.`);
      } finally {
        setLoading(false);
      }
    };

    loadForms();
  }, [mode, config.basePath, config.loadMethod]);

  const handleDeleteDraft = async (formId, isSubform = false) => {
    if (
      !window.confirm(
        `Are you sure you want to delete this ${
          isSubform ? "subform" : "form"
        } draft?`
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${DRAFT_DELETE_URL}/${formId}?is_subform=${isSubform}`,
        {
          method: "DELETE",
        }
      );

      if (response.status === 303) {
        // Handle redirect if the form was found in a different location
        const data = await response.json();
        const redirectResponse = await fetch(data.redirect, {
          method: "DELETE",
        });

        if (!redirectResponse.ok) {
          const errorData = await redirectResponse.json();
          throw new Error(
            errorData.message || "Failed to delete draft after redirect"
          );
        }
      } else if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete draft");
      }

      setForms(forms.filter((form) => form.form_id !== formId));
    } catch (error) {
      console.error("Error deleting draft:", error);
      alert("Failed to delete draft: " + error.message);
    }
  };

  const filteredForms = forms.filter((form) => {
    if (!searchTerm) return true;
    return (
      form.form_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      form.title?.en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (form.isSubform && "subform".includes(searchTerm.toLowerCase()))
    );
  });

  const getFormStats = (form) => ({
    questions: form.questions?.length || 0,
    languages: form.supported_languages?.length || 0,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading {config.title}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-bold text-red-600 mb-4">{error}</h2>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const getFormattedDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = parseHKTDatetimeStr(dateStr);
    const yyyy_mm_dd = getHongKongDateStr(d);
    return yyyy_mm_dd;
  };

  // Update the time formatting function
  const getFormattedDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = parseHKTDatetimeStr(dateStr);
    const yyyy_mm_dd_HH_MM_SS = getHongKongTimeStr(d);
    return yyyy_mm_dd_HH_MM_SS;
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold mb-4">{config.title} Dashboard</h1>
          {config.badge && (
            <div
              className={`px-3 py-1 bg-${config.badgeColor}-100 text-${config.badgeColor}-800 rounded-full text-sm flex items-center gap-2`}
            >
              <config.icon size={16} />
              {config.badge}
            </div>
          )}
        </div>
        <div className="flex items-center gap-4">
          <input
            type="text"
            placeholder={`Search ${config.title.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 max-w-md p-2 border rounded-lg"
          />
          <span className="text-gray-500">
            {filteredForms.length} form{filteredForms.length !== 1 ? "s" : ""}{" "}
            found
          </span>
        </div>
      </div>

      {/* Form Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredForms.map((form) => {
          const stats = getFormStats(form);
          /* Form Card */
          return (
            <div
              key={form.form_id}
              className={`group border rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col
    ${
      mode === "test"
        ? "border-yellow-200"
        : mode === "draft"
        ? "border-blue-200"
        : form.isSubform
        ? "border-blue-200"
        : ""
    }`}
            >
              {/* Content area - structured with fixed height sections */}
              <div className="p-3 flex flex-col flex-grow">
                {/* Title Section - Fixed height for 4 lines */}
                <div className="mb-2 h-12">
                  <FormTitle
                    title={form.title?.en}
                    formId={form.form_id}
                    isSubform={form.isSubform}
                  />
                </div>

                {/* Form ID, Last Update, and Language Tags Row - Fixed height */}
                <div className="flex items-center gap-2 mb-2 h-5">
                  <div className="flex items-center gap-2 text-xs text-gray-500 min-w-0 flex-1">
                    <span className="truncate font-medium">{form.form_id}</span>
                    {form.lastUpdate && (
                      <>
                        <span className="text-gray-300">•</span>
                        <div
                          className="flex items-center gap-1"
                          title={`Last updated: ${getFormattedDate(
                            form.lastUpdate
                          )}`}
                        >
                          <Pencil size={10} className="flex-shrink-0" />
                          <span className="truncate">
                            {getFormattedDate(form.lastUpdate)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  {/* Language Tags - aligned to right */}
                  <div className="flex flex-wrap gap-1 justify-end">
                    {form.supported_languages?.map((lang) => (
                      <span
                        key={lang}
                        className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-600 border border-blue-100"
                      >
                        {lang.toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Logo and Banner Row - Fixed height for all cards */}
                <div className="h-12 mb-2">
                  {!form.isSubform ? (
                    <div className="flex gap-2 h-full">
                      {/* Logo */}
                      <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-gray-50">
                        {form.logo_url?.en || form.logo_base64?.en ? (
                          <img
                            src={form.logo_url?.en || form.logo_base64?.en}
                            alt="Logo"
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Users size={20} className="text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Banner */}
                      <div className="flex-1 h-12 rounded-lg overflow-hidden bg-gray-100">
                        {form.banner_url?.en || form.banner_base64?.en ? (
                          <img
                            src={form.banner_url?.en || form.banner_base64?.en}
                            alt={form.title?.en || "Form banner"}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                            <span className="text-gray-400 text-xs">
                              No banner
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* Subform indicator - same height as logo/banner row */
                    <div className="h-full flex items-center">
                      <div className="py-1.5 px-2 bg-blue-50 rounded-lg">
                        <span className="text-xs font-medium text-blue-700">
                          Subform
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Spacer to push buttons to bottom */}
                <div className="flex-grow"></div>

                {/* Actions - always at bottom with fixed height */}
                <div className="flex gap-1 pt-2 border-t mt-auto h-10">
                  <Link
                    to={
                      form.isSubform
                        ? `/admin/${mode}/preview/subforms/${form.form_id}`
                        : `/admin/${mode}/preview/${form.form_id}`
                    }
                    className="flex items-center justify-center gap-1 px-2 py-1.5 flex-1 rounded-md bg-blue-50 text-blue-800 hover:bg-blue-100 transition-colors text-xs font-medium"
                  >
                    <Eye size={14} />
                    <span>Preview</span>
                  </Link>
                  <Link
                    to={
                      form.isSubform
                        ? `/admin/${mode}/edit/subforms/${form.form_id}`
                        : `/admin/${mode}/edit/${form.form_id}`
                    }
                    className="flex items-center justify-center gap-1 px-2 py-1.5 flex-1 rounded-md text-gray-600 hover:bg-gray-50 transition-colors text-xs font-medium"
                  >
                    <Edit size={14} />
                    <span>Inspect</span>
                  </Link>
                  {!form.isSubform && (
                    <Link
                      to={`/${mode}/${form.form_id}`}
                      target="_blank"
                      className="flex items-center justify-center px-2 py-1.5 rounded-md text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      <ExternalLink size={14} />
                    </Link>
                  )}
                  <button
                    onClick={() =>
                      handleDeleteDraft(form.form_id, form.isSubform)
                    }
                    disabled={mode !== "draft"}
                    className={`flex items-center justify-center px-2 py-1.5 rounded-md transition-colors
    ${
      mode === "draft"
        ? "text-red-600 hover:bg-red-50"
        : "text-gray-400 cursor-not-allowed"
    }`}
                    title={`Delete ${
                      form.isSubform ? "subform" : "form"
                    } draft`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredForms.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          {searchTerm ? (
            <>
              <p className="text-lg mb-2">
                No {config.title.toLowerCase()} found matching "{searchTerm}"
              </p>
              <p>Try adjusting your search terms</p>
            </>
          ) : (
            <>
              <p className="text-lg mb-2">
                No {config.title.toLowerCase()} available
              </p>
              <p>
                {mode === "test"
                  ? "Add test form files to the test directory to get started"
                  : mode === "draft"
                  ? "Save a form as draft to see it here"
                  : "Create your first form to get started"}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FormDashboard;
